import React, { useState } from 'react';
import { Palette, Image as ImageIcon, Sparkles, Check, RotateCcw, Eye, Lock, UploadCloud, RefreshCw } from 'lucide-react';
import { ClientEntity } from '../types';
import { ClientBranding, adjustColorBrightness } from '../utils/brandExtractor';
import { toast } from 'sonner';
import { db } from '../services/db';

interface BrandCustomizerProps {
  client: ClientEntity;
  branding: ClientBranding;
  onUpdateBranding: (newBranding: ClientBranding) => void;
}

const PRESET_PALETTES = [
  { name: 'Negócios & Tecnologia (Indigo)', primary: '#4f46e5', secondary: '#06b6d4' },
  { name: 'Saúde & Bem-Estar (Esmeralda)', primary: '#059669', secondary: '#10b981' },
  { name: 'Consultoria & Confiança (Azul Safira)', primary: '#2563eb', secondary: '#38bdf8' },
  { name: 'Estética & Beleza (Rosa Nobre)', primary: '#db2777', secondary: '#f43f5e' },
  { name: 'Luxo & Gastronomia (Âmbar & Ouro)', primary: '#d97706', secondary: '#f59e0b' },
  { name: 'Executivo Minimalista (Grafite)', primary: '#18181b', secondary: '#64748b' },
];

export const BrandCustomizer: React.FC<BrandCustomizerProps> = ({
  client,
  branding,
  onUpdateBranding
}) => {
  const [primaryColor, setPrimaryColor] = useState(branding.primaryColor);
  const [secondaryColor, setSecondaryColor] = useState(branding.secondaryColor);
  const [logoUrl, setLogoUrl] = useState(branding.logoUrl || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated: ClientBranding = {
        ...branding,
        primaryColor,
        secondaryColor,
        logoUrl: logoUrl.trim() || null,
        hasCustomTheme: true
      };

      // Save to localStorage for instant persistence
      localStorage.setItem(`client_branding_${client.id}`, JSON.stringify(updated));

      // Also persist to client brain if possible
      const brain = await db.getClientBusinessBrain(client.id) || {};
      brain.branding_custom = updated;
      await db.saveClientBusinessBrain(client.id, brain);

      onUpdateBranding(updated);
      toast.success('Identidade visual personalizada com sucesso!');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar customização da marca.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    localStorage.removeItem(`client_branding_${client.id}`);
    toast.info('Identidade restaurada para o padrão detectado do site.');
    window.location.reload();
  };

  const handlePresetSelect = (preset: typeof PRESET_PALETTES[0]) => {
    setPrimaryColor(preset.primary);
    setSecondaryColor(preset.secondary);
    toast.success(`Paleta "${preset.name}" aplicada.`);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Identidade Visual & White-Label</h2>
        <p className="text-gray-500 text-sm mt-1">
          Personalize a aparência de todo o seu painel de controle e portais com a marca da sua empresa.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Logo Section */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
              <ImageIcon size={18} style={{ color: primaryColor }} />
              Logotipo da Sua Empresa
            </h3>
            <p className="text-xs text-gray-500">
              A logo é exibida no topo do painel, na tela de login dos seus clientes e nos relatórios.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-20 h-20 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center p-2 overflow-hidden shrink-0 shadow-inner">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" />
                ) : (
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl text-white shadow-sm"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {branding.brandInitial}
                  </div>
                )}
              </div>

              <div className="flex-1 w-full space-y-2">
                <label className="text-xs font-bold text-gray-700 block">URL da Imagem da Logo</label>
                <input
                  type="text"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://suaempresa.com.br/logo.png"
                  className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black"
                />
                <span className="text-[11px] text-gray-400 block">
                  Dica: Você pode usar o link direto de uma imagem hospedada no seu site.
                </span>
              </div>
            </div>
          </div>

          {/* Color Palette Section */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-5">
            <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
              <Palette size={18} style={{ color: primaryColor }} />
              Design System & Cores da Marca
            </h3>
            <p className="text-xs text-gray-500">
              As cores serão aplicadas automaticamente a botões, indicadores de status, menus e botões de ação do painel.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1.5">Cor Primária (Destaques & Ações)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-12 h-10 rounded-lg cursor-pointer border border-gray-300 p-0.5"
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="flex-1 text-xs font-mono uppercase px-3 py-2 rounded-xl border border-gray-300 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1.5">Cor Secundária (Gradientes & Badges)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="w-12 h-10 rounded-lg cursor-pointer border border-gray-300 p-0.5"
                  />
                  <input
                    type="text"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="flex-1 text-xs font-mono uppercase px-3 py-2 rounded-xl border border-gray-300 outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Presets */}
            <div className="pt-2">
              <span className="text-xs font-bold text-gray-700 block mb-2">Paletas Rápidas Inspiradoras:</span>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {PRESET_PALETTES.map((preset, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handlePresetSelect(preset)}
                    className="flex items-center gap-2 p-2 rounded-xl border border-gray-200 hover:border-gray-400 bg-gray-50 text-left transition-all group"
                  >
                    <div className="flex -space-x-1">
                      <div className="w-4 h-4 rounded-full border border-white" style={{ backgroundColor: preset.primary }} />
                      <div className="w-4 h-4 rounded-full border border-white" style={{ backgroundColor: preset.secondary }} />
                    </div>
                    <span className="text-[11px] font-semibold text-gray-700 truncate group-hover:text-black">
                      {preset.name.split(' ')[0]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between gap-4 pt-2">
            <button
              type="button"
              onClick={handleReset}
              className="text-xs font-bold text-gray-500 hover:text-red-600 flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <RotateCcw size={14} /> Restaurar Padrão do Site
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="text-xs font-bold text-white px-6 py-3 rounded-xl shadow-md transition-all flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {isSaving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              Salvar Identidade Visual
            </button>
          </div>
        </div>

        {/* Live Preview Card */}
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1">
                <Eye size={13} /> Prévia da Marca
              </span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-emerald-800 bg-emerald-100">
                Ao Vivo
              </span>
            </div>

            {/* Mock Header */}
            <div className="p-4 rounded-xl border border-gray-100 bg-gray-50 space-y-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center font-bold text-white shadow-sm overflow-hidden"
                  style={{ backgroundColor: primaryColor }}
                >
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo" className="w-full h-full object-contain p-1" />
                  ) : (
                    <span>{branding.brandInitial}</span>
                  )}
                </div>
                <div>
                  <h4 className="text-xs font-bold text-gray-900 leading-tight">{client.data.name}</h4>
                  <p className="text-[10px] text-gray-500">Sistema Proprietário</p>
                </div>
              </div>

              {/* Mock Buttons */}
              <div className="space-y-2 pt-2">
                <button
                  className="w-full py-2 rounded-lg text-xs font-bold text-white shadow-sm text-center"
                  style={{ backgroundColor: primaryColor }}
                >
                  Botão Principal
                </button>
                <div
                  className="w-full py-1.5 rounded-lg text-[11px] font-bold text-center border"
                  style={{ color: primaryColor, borderColor: primaryColor, backgroundColor: adjustColorBrightness(primaryColor, 85) }}
                >
                  Destaque Secundário
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-900 text-white space-y-2 text-xs">
              <div className="flex items-center gap-2 font-bold text-amber-400">
                <Sparkles size={14} /> White-Label Ativo
              </div>
              <p className="text-[11px] text-slate-300 leading-relaxed">
                Nenhum cliente seu verá marcas de fornecedores. Todo o ecossistema funcionará como se tivesse sido desenvolvido exclusivamente pela sua empresa.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
